/**
 * Ported VERBATIM from apr_template `src/pages/BenefitRealization.tsx` (Planning & Integration).
 * Only import paths change, plus anything noted inline as a PORT NOTE.
 */
import React, { useState, useEffect, useRef } from 'react';
import PageHeader from '../compat/PageHeader';
import { authFetch } from '../compat/api';
import { listDispositions, type AppDisposition } from '../compat/dispositionsApi';
import './ApplicationDependencies.css';
import HelpPanel from '../compat/HelpPanel';
import { dispositionColor as matrixColor, matrixName } from '../dispositionPalette';
import type { Help } from '../compat/helpTypes';
// Decision Workspace design (owner ruling 2026-10-21): kit icons and token pairs; disposition
// chips keep the Disposition Matrix fill.
import { Banner, Icon, IconButton, icons } from '@platform/ui-kit/workspace';
import { WS_CARD, WS_CHIP, WS_COLOR, WS_RADIUS, WS_STATUS, wsTone, wsType } from '../workspace/ws';
import { useWsDialogs } from '../workspace/useWsDialogs';
import { GroupMultiSelect, ALL_GROUPS_LABEL, inPicked } from '../workspace/GroupMultiSelect';

const BenefitRealizationHelp: Help = {
  title: 'Benefit Realization',
  icon: '📈',
  purpose: 'Track the business case over time &mdash; savings, ROI, and break-even for each application by weighing current run cost against one-time disposition/project costs and future run rate.',
  doHere: [
    'Click <b>Recalculate All</b> to refresh savings, ROI, and break-even for every record.',
    'Click a currency cell to <b>inline-edit</b> Future Run Rate, Planned Project Cost, or Lease Buyback (Disposition Cost is read-only, sourced from cost analysis).',
    'Pick one or more <b>Integration Groups</b> (Ctrl-click for several), then filter by <b>Disposition</b>, <b>Application</b>, <b>TCO Data</b>, or <b>TCO Status</b>; stat cards, group business cases and totals follow the filter.',
    'Each group starts with its <b>group business case</b> row: every application in the group netted together — savings of the retiring apps against the cost of the whole group.',
    'Use <b>Download CSV</b> to export the current view, including 1/2/3-year savings and ROI.',
    'If empty, use <b>Initialize from Disposition Analysis</b> to seed records.',
  ],
  fits: 'Integration &rarr; Business Case &rarr; Benefit Realization',
  firstTime: 'If no data appears, click Initialize from Disposition Analysis, then Recalculate All to compute savings and ROI.',
  sections: [
    {
      type: 'defs',
      label: 'Key columns',
      items: [
        { term: 'Total Annual Cost', color: '#1e40af', def: 'Current TCO / run rate for the application today.' },
        { term: 'Disposition Cost', color: '#7c3aed', def: 'One-time cost to dispose (read-only, from Disposition Cost Analysis).' },
        { term: 'Net Savings', color: '#166534', def: 'Annual saving = current cost minus future run rate.' },
        { term: 'Break Even', color: '#dc2626', def: 'Months for cumulative savings to repay the up-front investment.' },
        { term: 'ROI %', color: '#e65100', def: 'Return over 1, 2, and 3 years against total costs.' },
      ],
      note: 'Future Run Rate is forced to 0 for a RETIRE disposition.',
    },
  ],
};

/* ─────────────────────────────────────────────────────────────────────────── */
/* Types                                                                        */
/* ─────────────────────────────────────────────────────────────────────────── */

interface BenefitRealizationRecord {
  // Primary Keys (grouping_id may come from backend but is ignored user-facing)
  grouping_id: string;
  application_id: string;
  application_name?: string;
  group_name?: string;

  // Input / editable fields
  disposition: 'RETIRE' | 'RE-PLATFORM' | 'RE-DESIGN' | 'RETAIN';
  total_annual_cost_current: number;
  total_disposition_cost: number;
  future_run_rate_annual: number;
  planned_project_cost: number;
  lease_buyback_benefit: number;
  months_horizon: number;

  // Calculated output fields (read-only)
  net_savings_value: number;
  net_savings_percent: number;
  adjusted_savings_value: number;
  adjusted_savings_percent: number;
  roi_percent: number;
  break_even_months_exact: number;

  // Audit fields
  created_at: string;
  updated_at: string;
  last_calculated_at: string;

  // Flags
  tco_missing?: boolean;
  tco_status?: string;

  // License Renegotiation fields
  license_renegotiation?: boolean;
  renegotiated_license_cost?: number;
  license_savings?: number;

  // Re-Platform sub-type flags
  consolidate?: boolean;
  replace_app?: boolean;
  consolidate_target_app?: string;
  replace_target_app?: string;
}

interface BenefitStatistics {
  totalApplications: number;
  totalTCO: number;
  totalMigrationCosts: number;
  totalAnnualSavings: number;
  avgBreakEvenMonths: number;
  totalOneYearSavings: number;
  totalTwoYearSavings: number;
  totalThreeYearSavings: number;
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* Helpers                                                                      */
/* ─────────────────────────────────────────────────────────────────────────── */

function parseNum(value: number | string | undefined | null): number {
  if (value === undefined || value === null) return 0;
  if (typeof value === 'number') return value;
  return parseFloat(value) || 0;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);
}

function formatPercentage(value: number): string {
  const percentValue = (value || 0) * 100;
  const truncated = Math.floor(percentValue * 100) / 100;
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(truncated) + '%';
}

function formatROIPercentage(value: number): string {
  const truncated = Math.floor((value || 0) * 100) / 100;
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(truncated) + '%';
}

function formatPortfolioROI(value: number): string {
  const percentValue = (value || 0) * 100;
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(percentValue) + '%';
}

function formatMonths(value: number | string): string {
  if (value === null || value === undefined) return 'N/A';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return 'N/A';
  if (num <= 0) return 'Immediate';
  return `${num.toFixed(1)} months`;
}

// PORT NOTE: the four colours come from the Disposition Matrix (§1.9.3 rule 3), not a
// hard-coded palette.
function dispositionColor(d: string): string {
  return matrixColor(d).base;
}

// DW: a disposition chip is FILLED in the Disposition Matrix colour (§1.9.3 rules 2-3); a
// value the matrix does not name (Pending, NEEDS_REVIEW) is the neutral pair.
function dispositionChipStyle(d?: string | null): React.CSSProperties {
  if (!d || !matrixName(d)) return { ...WS_CHIP, ...wsTone('neutral') };
  const c = matrixColor(d);
  return { ...WS_CHIP, background: c.base, color: c.onBase };
}

/** Numbers in table columns: right-aligned, tabular figures. */
const TABULAR: React.CSSProperties = { textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

/**
 * DW (DESIGN.md, Business Case screens): a PROJECTED figure — the Future Run Rate (the
 * screen's own "Estimated annual cost … after initiative is complete") and the savings derived
 * from it — carries a dashed underline; the measured current TCO stays plain.
 */
// Owner ruling 2026-11-04: "no data should be underlined" — projected figures are plain like the rest.
const PROJECTED: React.CSSProperties = {};

/** A signed figure's cell: success pair above zero, error pair below, plain at zero. */
function signedCell(v: number): React.CSSProperties {
  if (v > 0) return wsTone('success');
  if (v < 0) return wsTone('error');
  return { background: WS_COLOR.surface, color: WS_COLOR.text };
}

/** A signed figure as text only (ROI): the status ink, or plain text at zero. */
function signedInk(v: number): string {
  if (v > 0) return WS_STATUS.success.ink;
  if (v < 0) return WS_STATUS.error.ink;
  return WS_COLOR.text;
}

/**
 * Collapse duplicate application_id records (from different historical
 * grouping_ids) — keep the most-recently-calculated one.
 */
function collapseByApp(records: BenefitRealizationRecord[]): BenefitRealizationRecord[] {
  const byApp = new Map<string, BenefitRealizationRecord>();
  for (const r of records) {
    const existing = byApp.get(r.application_id);
    if (!existing) {
      byApp.set(r.application_id, r);
    } else {
      const existingTs = existing.last_calculated_at
        ? new Date(existing.last_calculated_at).getTime()
        : new Date(existing.updated_at || 0).getTime();
      const newTs = r.last_calculated_at
        ? new Date(r.last_calculated_at).getTime()
        : new Date(r.updated_at || 0).getTime();
      if (newTs > existingTs) byApp.set(r.application_id, r);
    }
  }
  return Array.from(byApp.values());
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* MultiSelectChips                                                             */
/* ─────────────────────────────────────────────────────────────────────────── */

interface MultiSelectProps {
  label: string;
  options: string[];
  selected: string[];
  onChange: (next: string[]) => void;
  renderOption?: (v: string) => React.ReactNode;
  /** Optional label transformer; defaults to identity. Lets `options` be opaque IDs while displaying names. */
  getLabel?: (v: string) => string;
  placeholder?: string;
}

function MultiSelectChips({
  label, options, selected, onChange, renderOption, getLabel, placeholder = 'All',
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toggle = (v: string) => {
    onChange(selected.includes(v) ? selected.filter(s => s !== v) : [...selected, v]);
  };

  const labelFor = (v: string) => (getLabel ? getLabel(v) : v);
  const filtered = options.filter(o => labelFor(o).toLowerCase().includes(search.toLowerCase()));

  return (
    <div ref={containerRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        className="rd-btn rd-btn-ghost"
        style={{
          display: 'flex', alignItems: 'center', gap: 6, minWidth: 120,
          border: selected.length ? `1px solid ${WS_COLOR.primary}` : undefined,
          color: selected.length ? WS_COLOR.primary : undefined,
        }}
        onClick={() => { setOpen(v => !v); setSearch(''); }}
      >
        <Icon icon={icons.Filter} size={16} />
        {label}
        {selected.length > 0 && (
          <span style={{
            background: WS_COLOR.primary, color: WS_COLOR.onPrimary,
            borderRadius: '50%', width: 18, height: 18,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            ...wsType('meta'), fontWeight: 600, flexShrink: 0,
          }}>
            {selected.length}
          </span>
        )}
        {/* DW: expand_less / expand_more → kit chevrons (the curated set has no ChevronUp). */}
        <Icon icon={open ? icons.ChevronDown : icons.ChevronRight} size={16} />
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 200, marginTop: 4,
          background: WS_COLOR.surface, border: `1px solid ${WS_COLOR.border}`, borderRadius: WS_RADIUS.field,
          boxShadow: WS_COLOR.menuShadow,
          minWidth: 240, maxHeight: 320, display: 'flex', flexDirection: 'column',
        }}>
          <div style={{ padding: '8px 10px', borderBottom: `1px solid ${WS_COLOR.border}` }}>
            <input
              className="rd-input"
              type="search"
              placeholder="Search…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ width: '100%' }}
              autoFocus
            />
          </div>

          <div style={{ overflowY: 'auto', flex: 1 }}>
            {filtered.length === 0 && (
              <div style={{ padding: '12px 14px', ...wsType('label'), color: WS_COLOR.textMuted }}>No matches</div>
            )}
            {filtered.map(opt => (
              <label
                key={opt}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 14px', cursor: 'pointer', ...wsType('label'),
                  background: selected.includes(opt) ? WS_COLOR.surfaceSubtle : undefined,
                }}
              >
                <input
                  type="checkbox"
                  checked={selected.includes(opt)}
                  onChange={() => toggle(opt)}
                  style={{ cursor: 'pointer' }}
                />
                {renderOption ? renderOption(opt) : labelFor(opt)}
              </label>
            ))}
          </div>

          <div style={{
            padding: '8px 14px', borderTop: `1px solid ${WS_COLOR.border}`,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <span style={{ ...wsType('meta'), color: WS_COLOR.textMuted }}>
              {selected.length === 0 ? placeholder : `${selected.length} selected`}
            </span>
            {selected.length > 0 && (
              <button
                className="rd-btn rd-btn-ghost"
                onClick={() => onChange([])}
              >
                Clear
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* Component                                                                    */
/* ─────────────────────────────────────────────────────────────────────────── */

const BenefitRealization: React.FC = () => {
  const [allRecords, setAllRecords] = useState<BenefitRealizationRecord[]>([]);
  const [dispositions, setDispositions] = useState<AppDisposition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterGroupIds, setFilterGroupIds] = useState<string[]>([]);
  const [filterApplicationIds, setFilterApplicationIds] = useState<string[]>([]);
  const [filterTCOData, setFilterTCOData] = useState<string[]>([]);          // [] = all; values: 'With TCO' | 'Without TCO'
  const [filterTCOStatuses, setFilterTCOStatuses] = useState<string[]>([]);  // [] = all
  const [filterDispositions, setFilterDispositions] = useState<string[]>([]);
  const [editingField, setEditingField] = useState<{ record: BenefitRealizationRecord; field: string } | null>(null);
  const [editValue, setEditValue] = useState('');
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipTimer, setTooltipTimer] = useState<NodeJS.Timeout | null>(null);
  const [showPlannedProjectTooltip, setShowPlannedProjectTooltip] = useState(false);
  const [plannedProjectTooltipTimer, setPlannedProjectTooltipTimer] = useState<NodeJS.Timeout | null>(null);
  const [portfolioMetrics, setPortfolioMetrics] = useState<any>(null);
  const [updateTrigger, setUpdateTrigger] = useState(0);
  const [approvedApplications, setApprovedApplications] = useState<Set<string>>(new Set());

  const { notify, dialogs } = useWsDialogs();

  // ── Data loading ──────────────────────────────────────────────────────────

  const fetchApprovedApplications = async () => {
    try {
      const response = await authFetch('/api/deployment-status');
      if (response.ok) {
        const data = await response.json();
        const approved = new Set<string>();
        data.forEach((item: any) => {
          if (item.deployment_status === 'Approved') approved.add(item.application_id);
        });
        setApprovedApplications(approved);
      }
    } catch (err) {
      console.warn('deployment-status endpoint not available, skipping approved-apps check');
    }
  };

  const loadBenefitRealization = async () => {
    try {
      setLoading(true);
      const response = await authFetch('/api/benefit-realization');
      if (!response.ok && response.status !== 404) {
        throw new Error('Failed to fetch benefit realization data');
      }
      const raw: BenefitRealizationRecord[] = response.ok ? await response.json() : [];
      const data = Array.isArray(raw) ? raw : [];
      // PORT NOTE (owner request 2026-11-04): the source collapsed an app's rows from several
      // groups into one; each (group, app) is now its own row, under its group's business case.
      setAllRecords(data);
      setError('');
    } catch (err: any) {
      console.error('Error loading benefit realization data:', err);
      setAllRecords([]);
      setError('');
    } finally {
      setLoading(false);
    }
  };

  const loadPortfolioMetrics = async () => {
    try {
      const response = await authFetch('/api/benefit-realization/portfolio-metrics');
      if (response.ok) {
        const data = await response.json();
        setPortfolioMetrics(data.portfolioMetrics);
      }
    } catch (err) {
      console.warn('Failed to load portfolio metrics:', err);
    }
  };

  const loadDispositions = async () => {
    try {
      const data = await listDispositions();
      setDispositions(data);
    } catch (err) {
      console.warn('Failed to load dispositions:', err);
    }
  };

  useEffect(() => {
    loadBenefitRealization();
    loadDispositions();
    fetchApprovedApplications();
  }, []);

  useEffect(() => {
    loadPortfolioMetrics();
  }, [allRecords]);

  // ── Disposition lookup map (keyed by application UUID = AppDisposition.id) ─

  const dispositionMap = React.useMemo(() => {
    const m = new Map<string, string | undefined>();
    dispositions.forEach(d => m.set(d.id, d.final_disposition));
    return m;
  }, [dispositions]);

  // Distinct final_disposition values from the dispositions list (plus "Pending")
  const dispositionOptions = React.useMemo(() => {
    const vals = new Set<string>();
    dispositions.forEach(d => {
      if (d.final_disposition) vals.add(d.final_disposition);
    });
    vals.add('Pending');
    return Array.from(vals).sort();
  }, [dispositions]);

  // ── Calculations ──────────────────────────────────────────────────────────

  /**
   * Owner ruling 2026-11-04: Net Savings is the ANNUAL saving — current TCO minus the future run
   * rate (or the licence saving on a renegotiation), as this screen's own definition says. The
   * source subtracted the one-time disposition and project costs as well, so a Retire app whose
   * one-time cost exceeded one year's saving showed a negative "saving". One-time costs stay in
   * the 1/2/3-year savings, ROI and break-even columns.
   */
  const annualSavingsOf = (record: BenefitRealizationRecord): number => {
    if (record.license_renegotiation && parseNum(record.renegotiated_license_cost) > 0) {
      return parseNum(record.license_savings);
    }
    return parseNum(record.total_annual_cost_current) - parseNum(record.future_run_rate_annual);
  };
  const annualSavingsPct = (record: BenefitRealizationRecord): number => {
    const tco = parseNum(record.total_annual_cost_current);
    return tco > 0 ? annualSavingsOf(record) / tco : 0;
  };

  const calculateAdjustedSavingsValue = (record: BenefitRealizationRecord): number => {
    return annualSavingsOf(record) + parseNum(record.lease_buyback_benefit);
  };
  const adjustedSavingsPct = (record: BenefitRealizationRecord): number => {
    const tco = parseNum(record.total_annual_cost_current);
    return tco > 0 ? calculateAdjustedSavingsValue(record) / tco : 0;
  };

  const calculateNYearSavings = (record: BenefitRealizationRecord, years: number): number => {
    if (record.license_renegotiation && parseNum(record.renegotiated_license_cost) > 0) {
      return parseNum(record.license_savings) * years
        - parseNum(record.planned_project_cost)
        - parseNum(record.total_disposition_cost)
        + parseNum(record.lease_buyback_benefit);
    }
    const TCO = parseNum(record.total_annual_cost_current);
    const FRC = parseNum(record.future_run_rate_annual);
    const DC  = parseNum(record.total_disposition_cost);
    const PPC = parseNum(record.planned_project_cost);
    const LB  = parseNum(record.lease_buyback_benefit);
    return (TCO - FRC) * years - DC - PPC + LB;
  };

  const calculateOneYearSavings   = (r: BenefitRealizationRecord) => calculateNYearSavings(r, 1);
  const calculateTwoYearSavings   = (r: BenefitRealizationRecord) => calculateNYearSavings(r, 2);
  const calculateThreeYearSavings = (r: BenefitRealizationRecord) => calculateNYearSavings(r, 3);

  /**
   * ROI — the Strategic Portfolio Navigator formula (owner ruling 2026-11-04, "use the SPN
   * formulas"; portfolio_definition/lib/financial.ts calculateFinancialMetrics):
   * ROI % = annual savings ÷ landed cost × 100, landed cost = the one-time costs (Disposition +
   * Planned Project). Over n years: n × annual savings ÷ landed cost × 100.
   */
  const calculateNYearROI = (record: BenefitRealizationRecord, years: number): number => {
    const landed = parseNum(record.total_disposition_cost) + parseNum(record.planned_project_cost);
    return landed > 0 ? ((annualSavingsOf(record) * years) / landed) * 100 : 0;
  };

  // ── Filtering ─────────────────────────────────────────────────────────────

  const filteredRecords = React.useMemo(() => {
    return allRecords.filter(record => {
      // Owner ruling 2026-11-04: only records that carry a disposition cost.
      if (!(parseNum(record.total_disposition_cost) > 0)) return false;
      // Integration Group filter (Ctrl-click multi-select; empty = all)
      if (!inPicked(filterGroupIds, record.grouping_id)) return false;
      // Application filter (multi-select; empty = all)
      if (filterApplicationIds.length > 0 && !filterApplicationIds.includes(record.application_id)) return false;

      // TCO Data filter (multi-select; empty = all)
      if (filterTCOData.length > 0) {
        const tco = parseFloat(String(record.total_annual_cost_current)) || 0;
        const tcoLabel = tco > 0 ? 'With TCO' : 'Without TCO';
        if (!filterTCOData.includes(tcoLabel)) return false;
      }

      // TCO Status filter (multi-select; empty = all)
      if (filterTCOStatuses.length > 0) {
        const status = record.tco_status || 'Not Defined';
        if (!filterTCOStatuses.includes(status)) return false;
      }

      // Disposition filter (from /api/dispositions lookup)
      if (filterDispositions.length > 0) {
        const finalDisp = dispositionMap.get(record.application_id);
        const dispLabel = finalDisp || 'Pending';
        if (!filterDispositions.includes(dispLabel)) return false;
      }

      return true;
    });
  }, [allRecords, filterGroupIds, filterApplicationIds, filterTCOData, filterTCOStatuses, filterDispositions, dispositionMap]);

  // Rows ordered by group, then application — each group opens with its business-case row.
  const groupedRecords = React.useMemo(
    () => [...filteredRecords].sort((a, b) => (a.grouping_id || '').localeCompare(b.grouping_id || '') || a.application_id.localeCompare(b.application_id)),
    [filteredRecords],
  );
  const groupOptions = React.useMemo(
    () => Array.from(new Map(allRecords.filter(r => parseNum(r.total_disposition_cost) > 0).map(r => [r.grouping_id, r.group_name])).entries())
      .filter(([id]) => !!id).map(([id, description]) => ({ id, description })).sort((a, b) => a.id.localeCompare(b.id)),
    [allRecords],
  );

  // ── Display stats (based on filtered records) ─────────────────────────────

  const displayStats: BenefitStatistics = React.useMemo(() => {
    const source = filteredRecords;
    const totalApps = source.length;
    // An application in several groups (APP-0544) has one TCO: count it once.
    const tcoByApp = new Map<string, number>();
    source.forEach(r => tcoByApp.set(r.application_id, parseNum(r.total_annual_cost_current)));
    const totalTCO = Array.from(tcoByApp.values()).reduce((sum, v) => sum + v, 0);
    const totalMigration = source.reduce((sum, r) => sum + parseNum(r.total_disposition_cost), 0);
    const totalSavings = source.reduce((sum, r) => sum + annualSavingsOf(r), 0);
    const totalAnnualSavings = source.reduce((sum, r) => {
      const TCO = parseNum(r.total_annual_cost_current);
      const FRC = parseNum(r.future_run_rate_annual);
      return sum + (TCO - FRC);
    }, 0);
    const totalNetInvestment = source.reduce((sum, r) => {
      const DC  = parseNum(r.total_disposition_cost);
      const PPC = parseNum(r.planned_project_cost);
      const LB  = parseNum(r.lease_buyback_benefit);
      return sum + (DC + PPC - LB);
    }, 0);
    const portfolioBreakEvenMonths = totalAnnualSavings > 0
      ? (totalNetInvestment / totalAnnualSavings) * 12
      : 0;

    const calcN = (r: BenefitRealizationRecord, n: number): number => {
      if (r.license_renegotiation && parseNum(r.renegotiated_license_cost) > 0) {
        return parseNum(r.license_savings) * n
          - parseNum(r.planned_project_cost)
          - parseNum(r.total_disposition_cost)
          + parseNum(r.lease_buyback_benefit);
      }
      const TCO = parseNum(r.total_annual_cost_current);
      const FRC = parseNum(r.future_run_rate_annual);
      const DC  = parseNum(r.total_disposition_cost);
      const PPC = parseNum(r.planned_project_cost);
      const LB  = parseNum(r.lease_buyback_benefit);
      return (TCO - FRC) * n - DC - PPC + LB;
    };

    return {
      totalApplications: totalApps,
      totalTCO,
      totalMigrationCosts: totalMigration,
      totalAnnualSavings: totalSavings,
      avgBreakEvenMonths: portfolioBreakEvenMonths,
      totalOneYearSavings: source.reduce((sum, r) => sum + calcN(r, 1), 0),
      totalTwoYearSavings: source.reduce((sum, r) => sum + calcN(r, 2), 0),
      totalThreeYearSavings: source.reduce((sum, r) => sum + calcN(r, 3), 0),
    };
  }, [filteredRecords]);

  // ── Record update / recalculate ───────────────────────────────────────────

  const updateRecord = async (record: BenefitRealizationRecord, field: string, value: any) => {
    try {
      const response = await authFetch(
        `/api/benefit-realization/${record.grouping_id}/${record.application_id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ [field]: value }),
        }
      );
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update record: ${response.status} ${errorText}`);
      }
      const responseData = await response.json();
      const { portfolioMetrics: updatedPM, ...updatedRecord } = responseData;
      setAllRecords(prev => {
        const updated = prev.map(r =>
          r.application_id === record.application_id ? updatedRecord : r
        );
        return collapseByApp(updated);
      });
      if (updatedPM) setPortfolioMetrics(updatedPM);
    } catch (err: any) {
      setError(err.message || 'Failed to update record');
    }
  };

  const recalculateRecord = async (record: BenefitRealizationRecord) => {
    try {
      const response = await authFetch(
        `/api/benefit-realization/recalculate/${record.grouping_id}/${record.application_id}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' } }
      );
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to recalculate: ${response.status} ${errorText}`);
      }
      const updatedRecord = await response.json();
      setAllRecords(prev => {
        const updated = prev.map(r =>
          r.application_id === record.application_id ? updatedRecord : r
        );
        return collapseByApp(updated);
      });
      setUpdateTrigger(prev => prev + 1);
    } catch (err: any) {
      console.error('Recalculation error:', err);
      setError(err.message || 'Failed to recalculate record');
    }
  };

  const recalculateAll = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await authFetch('/api/benefit-realization/recalculate-all', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to recalculate all records: ${response.status} ${errorText}`);
      }
      const result = await response.json();
      if (result.records && result.records.length > 0) {
        setAllRecords(collapseByApp([...result.records]));
        setUpdateTrigger(prev => prev + 1);
        if (result.portfolioMetrics) setPortfolioMetrics(result.portfolioMetrics);
      } else {
        await loadBenefitRealization();
      }
    } catch (err: any) {
      console.error('Recalculate all error:', err);
      setError(err.message || 'Failed to recalculate all records');
    } finally {
      setLoading(false);
    }
  };

  const initializeFromDispositionAnalysis = async () => {
    try {
      const response = await authFetch('/api/benefit-realization/initialize-from-disposition-analysis', {
        method: 'POST',
      });
      if (!response.ok && response.status !== 404) {
        throw new Error('Failed to initialize benefit realization data');
      }
      if (response.ok) {
        await loadBenefitRealization();
      } else {
        setAllRecords([]);
        setError('');
      }
    } catch (err: any) {
      console.error('Error initializing benefit realization data:', err);
      setAllRecords([]);
      setError('');
    }
  };

  // ── Inline editing ────────────────────────────────────────────────────────

  const handleEditStart = (record: BenefitRealizationRecord, field: string) => {
    setEditingField({ record, field });
    setEditValue(String(record[field as keyof BenefitRealizationRecord]));
  };

  const handleEditSave = async () => {
    if (!editingField) return;
    const { record, field } = editingField;
    const numericValue = parseFloat(editValue) || 0;
    await updateRecord(record, field, numericValue);
    setEditingField(null);
  };

  const handleEditCancel = () => {
    setEditingField(null);
    setEditValue('');
  };

  const isEditing = (record: BenefitRealizationRecord, field: string) =>
    editingField?.record.application_id === record.application_id &&
    editingField?.field === field;

  const renderEditableCell = (
    record: BenefitRealizationRecord,
    field: keyof BenefitRealizationRecord,
    value: number
  ) => {
    const isDispositionCostLocked = field === 'total_disposition_cost';
    const isNonEditableForRetire  = field === 'future_run_rate_annual' && record.disposition === 'RETIRE';
    const isNonEditable = isDispositionCostLocked || isNonEditableForRetire;

    if (isEditing(record, field)) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <input
            type="text"
            inputMode="decimal"
            value={editValue}
            onChange={e => setEditValue(e.target.value)}
            style={{ width: '130px' }}
            autoFocus
            onKeyDown={e => {
              if (e.key === 'Enter') handleEditSave();
              if (e.key === 'Escape') handleEditCancel();
            }}
          />
          <IconButton icon={icons.Check} label="Save" onClick={handleEditSave} />
          <IconButton icon={icons.X} label="Cancel" onClick={handleEditCancel} />
        </div>
      );
    }

    return (
      <div
        onClick={() => !isNonEditable && handleEditStart(record, field)}
        style={{
          cursor: isNonEditable ? 'not-allowed' : 'pointer',
          padding: '2px 4px', borderRadius: WS_RADIUS.chip,
          backgroundColor: isNonEditable ? WS_COLOR.surfaceSubtle : 'transparent',
          color: isNonEditable ? WS_COLOR.textMuted : 'inherit',
          fontVariantNumeric: 'tabular-nums',
          ...(field === 'future_run_rate_annual' ? PROJECTED : null),
        }}
        title={
          isDispositionCostLocked
            ? 'Disposition Cost is read-only — sourced from Disposition Cost Analysis'
            : isNonEditableForRetire
              ? 'Future Run Rate must be 0 for RETIRE disposition'
              : 'Click to edit'
        }
        onMouseEnter={e => { if (!isNonEditable) (e.currentTarget as HTMLDivElement).style.backgroundColor = WS_COLOR.tonalHover; }}
        onMouseLeave={e => { if (!isNonEditable) (e.currentTarget as HTMLDivElement).style.backgroundColor = 'transparent'; }}
      >
        {formatCurrency(value)}
      </div>
    );
  };

  // ── CSV Export ────────────────────────────────────────────────────────────

  const exportToCSV = () => {
    const recordsToExport = filteredRecords;
    if (recordsToExport.length === 0) { notify('No data to download', 'info'); return; }

    const headers = [
      'Application ID', 'Application Name', 'Disposition',
      'Final Disposition (from Dispositions)',
      'Total Annual Cost', 'Total Disposition Cost',
      'Future Run Rate Costs', 'Planned Project Costs',
      'Net Savings', 'Net Savings %', 'Lease Buyback',
      'Adjusted Savings', 'Adjusted %',
      'Break Even (Months)',
      '1-Year Savings', 'ROI % (over 1 year)',
      '2-Year Savings', 'ROI % (over 2 years)',
      '3-Year Savings', 'ROI % (over 3 years)',
    ];

    const rows = recordsToExport.map(r => [
      r.application_id,
      r.application_name || '',
      r.disposition,
      dispositionMap.get(r.application_id) || 'Pending',
      parseNum(r.total_annual_cost_current),
      parseNum(r.total_disposition_cost),
      parseNum(r.future_run_rate_annual),
      parseNum(r.planned_project_cost),
      annualSavingsOf(r),
      annualSavingsPct(r),
      parseNum(r.lease_buyback_benefit),
      calculateAdjustedSavingsValue(r),
      adjustedSavingsPct(r),
      parseNum(r.break_even_months_exact),
      calculateOneYearSavings(r),
      calculateNYearROI(r, 1),
      calculateTwoYearSavings(r),
      calculateNYearROI(r, 2),
      calculateThreeYearSavings(r),
      calculateNYearROI(r, 3),
    ]);

    const totalCount = recordsToExport.length;
    const grandTotal = [
      'GRAND TOTAL', `${totalCount} Applications`, '', '',
      recordsToExport.reduce((s, r) => s + parseNum(r.total_annual_cost_current), 0),
      recordsToExport.reduce((s, r) => s + parseNum(r.total_disposition_cost), 0),
      recordsToExport.reduce((s, r) => s + parseNum(r.future_run_rate_annual), 0),
      recordsToExport.reduce((s, r) => s + parseNum(r.planned_project_cost), 0),
      recordsToExport.reduce((s, r) => s + annualSavingsOf(r), 0),
      '',
      recordsToExport.reduce((s, r) => s + parseNum(r.lease_buyback_benefit), 0),
      recordsToExport.reduce((s, r) => s + calculateAdjustedSavingsValue(r), 0),
      '',
      totalCount > 0
        ? (recordsToExport.reduce((s, r) => s + parseNum(r.break_even_months_exact), 0) / totalCount).toFixed(1)
        : 0,
      recordsToExport.reduce((s, r) => s + calculateOneYearSavings(r), 0),
      '',
      recordsToExport.reduce((s, r) => s + calculateTwoYearSavings(r), 0),
      '',
      recordsToExport.reduce((s, r) => s + calculateThreeYearSavings(r), 0),
      '',
    ];

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(',')),
      '',
      grandTotal.join(','),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'benefit-realization-analysis.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Group business case (owner request 2026-11-04) ─────────────────────────
  // Every application of the group netted together, with the row formulas summed: the savings of
  // the retiring apps against the disposition cost of the whole group (the Retain platform's build
  // included). Break-even = (DC + PPC − LB) ÷ annual savings × 12; ROI(n) = n-year savings ÷ (DC + PPC).
  const groupBusinessCaseRow = (groupId: string, groupName?: string) => {
    const rs = filteredRecords.filter(r => r.grouping_id === groupId);
    const sum = (fn: (r: BenefitRealizationRecord) => number) => rs.reduce((acc, r) => acc + fn(r), 0);
    const tco = sum(r => parseNum(r.total_annual_cost_current));
    const dc = sum(r => parseNum(r.total_disposition_cost));
    const frc = sum(r => parseNum(r.future_run_rate_annual));
    const ppc = sum(r => parseNum(r.planned_project_cost));
    const lb = sum(r => parseNum(r.lease_buyback_benefit));
    const net = sum(r => annualSavingsOf(r));
    const reneg = sum(r => parseNum(r.renegotiated_license_cost));
    const lic = sum(r => parseNum(r.license_savings));
    const adj = sum(r => calculateAdjustedSavingsValue(r));
    const annual = sum(r => (r.license_renegotiation && parseNum(r.renegotiated_license_cost) > 0)
      ? parseNum(r.license_savings)
      : parseNum(r.total_annual_cost_current) - parseNum(r.future_run_rate_annual));
    const invest = dc + ppc - lb;
    const breakEven = annual > 0 ? (invest / annual) * 12 : 0;
    const nYear = (n: number) => annual * n - invest;
    const roi = (n: number) => (dc + ppc) > 0 ? ((annual * n) / (dc + ppc)) * 100 : 0; // SPN: n × annual savings ÷ landed cost
    const pct = (v: number) => `${v.toFixed(2)}%`;
    const money = (v: number) => (
      <span style={{ color: v < 0 ? WS_STATUS.error.ink : v > 0 ? WS_STATUS.success.ink : WS_COLOR.text }}>{formatCurrency(v)}</span>
    );
    const cell: React.CSSProperties = { ...TABULAR, fontWeight: 600 };
    return (
      <tr data-testid="br-group-row" style={{ backgroundColor: WS_COLOR.surfaceSubtle }}>
        <td style={{ position: 'sticky', left: 0, width: '200px', maxWidth: '200px', boxSizing: 'border-box', backgroundClip: 'padding-box', padding: 6, backgroundColor: WS_COLOR.surfaceSubtle, borderRight: `2px solid ${WS_COLOR.border}`, minWidth: '200px', zIndex: 5 }}>
          <div style={{ ...wsType('mono'), fontWeight: 600, color: WS_COLOR.text }}>{groupId}</div>
          {groupName && <div style={{ ...wsType('meta'), color: WS_COLOR.textMuted }}>{groupName}</div>}
        </td>
        <td style={{ position: 'sticky', left: '200px', width: '140px', maxWidth: '140px', boxSizing: 'border-box', backgroundClip: 'padding-box', padding: 6, backgroundColor: WS_COLOR.surfaceSubtle, borderRight: `2px solid ${WS_COLOR.border}`, textAlign: 'center', zIndex: 4, minWidth: '140px' }}>
          <span style={{ ...WS_CHIP, ...wsTone('info') }}>Group business case</span>
          <div style={{ ...wsType('meta'), color: WS_COLOR.textMuted, marginTop: 2 }}>{rs.length} app{rs.length !== 1 ? 's' : ''}</div>
        </td>
        <td style={cell}>{formatCurrency(tco)}</td>
        <td style={cell}>{formatCurrency(dc)}</td>
        <td style={cell}>{formatCurrency(frc)}</td>
        <td style={cell}>{formatCurrency(ppc)}</td>
        <td style={cell}>{money(net)}</td>
        <td style={cell}>{tco > 0 ? pct((net / tco) * 100) : '—'}</td>
        <td style={cell}>{formatCurrency(lb)}</td>
        <td style={cell}>{reneg ? formatCurrency(reneg) : '—'}</td>
        <td style={cell}>{lic ? formatCurrency(lic) : '—'}</td>
        <td style={cell}>{money(adj)}</td>
        <td style={cell}>{tco > 0 ? pct((adj / tco) * 100) : '—'}</td>
        <td style={cell} data-testid="br-group-breakeven">{annual > 0 ? `${breakEven.toFixed(1)} mo` : 'Never'}</td>
        <td style={cell}>{money(nYear(1))}</td>
        <td style={cell}>{pct(roi(1))}</td>
        <td style={cell}>{money(nYear(2))}</td>
        <td style={cell}>{pct(roi(2))}</td>
        <td style={cell}>{money(nYear(3))}</td>
        <td style={cell}>{pct(roi(3))}</td>
        <td />
      </tr>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const hasActiveFilters =
    filterGroupIds.length > 0 ||
    filterApplicationIds.length > 0 ||
    filterTCOData.length > 0 ||
    filterTCOStatuses.length > 0 ||
    filterDispositions.length > 0;

  if (loading) {
    return (
      <div className="rd-page">
        <PageHeader title="Benefit Realization" subtitle="ROI, savings, and break-even analysis across the application portfolio" />
        <div className="md-empty-state">
          <div className="md-empty-icon"><Icon icon={icons.Loader2} size={24} /></div>
          <h3>Loading Benefit Realization Data</h3>
          <p>Please wait while we fetch the data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rd-page">
        <PageHeader title="Benefit Realization" subtitle="ROI, savings, and break-even analysis across the application portfolio" />
        <Banner
          tone="error"
          action={
            <button className="btn" onClick={loadBenefitRealization}>
              Retry
            </button>
          }
        >
          Error: {error}
        </Banner>
      </div>
    );
  }

  return (
    <div className="page page-fixed-header">
      <HelpPanel help={BenefitRealizationHelp} />
      {/* ── Fixed header section ──────────────────────────────────────────── */}
      <div className="fixed-header-section">
        <PageHeader
          title="Benefit Realization"
          subtitle="ROI, savings, and break-even analysis across the application portfolio"
          actions={
            <>
              <button
                onClick={recalculateAll}
                disabled={loading || allRecords.length === 0}
                className="rd-btn rd-btn-primary"
                title="Recalculate all benefit analysis values"
              >
                {loading ? 'Recalculating...' : <><Icon icon={icons.RefreshCw} size={20} /> Recalculate All</>}
              </button>
              {allRecords.length > 0 && (
                <button
                  onClick={exportToCSV}
                  className="rd-btn rd-btn-ghost"
                  title="Download current view as CSV"
                >
                  <Icon icon={icons.Download} size={20} />
                  Download CSV
                </button>
              )}
            </>
          }
        />

        {/* ── Stat strip ──────────────────────────────────────────────────── */}
        {allRecords.length > 0 && (
          <div className="rd-stat-grid rd-stat-grid--compact" style={{ marginBottom: 12 }}>
            <div className="rd-stat-card">
              <div className="rd-stat-label">Applications</div>
              <div className="rd-stat-value" style={{ color: WS_COLOR.primary }}>
                {displayStats.totalApplications}
              </div>
            </div>
            <div className="rd-stat-card">
              <div className="rd-stat-label">TCO (Total Annual Cost)</div>
              <div className="rd-stat-value">{formatCurrency(displayStats.totalTCO)}</div>
            </div>
            <div className="rd-stat-card">
              <div className="rd-stat-label">Migration Costs</div>
              <div className="rd-stat-value">{formatCurrency(displayStats.totalMigrationCosts)}</div>
            </div>
            <div className="rd-stat-card">
              <div className="rd-stat-label">Annual Savings</div>
              <div className="rd-stat-value" style={{ color: WS_STATUS.success.ink, ...PROJECTED }}>
                {formatCurrency(displayStats.totalAnnualSavings)}
              </div>
            </div>
            <div className="rd-stat-card">
              <div className="rd-stat-label">Break-Even (Portfolio)</div>
              <div className="rd-stat-value" style={{ color: WS_COLOR.primary }}>
                {formatMonths(displayStats.avgBreakEvenMonths)}
              </div>
            </div>
            <div className="rd-stat-card">
              <div className="rd-stat-label">1-Year Savings</div>
              <div className="rd-stat-value" style={{ color: WS_STATUS.success.ink, ...PROJECTED }}>
                {formatCurrency(displayStats.totalOneYearSavings)}
              </div>
            </div>
            <div className="rd-stat-card">
              <div className="rd-stat-label">2-Year Savings</div>
              <div className="rd-stat-value" style={{ color: WS_STATUS.success.ink, ...PROJECTED }}>
                {formatCurrency(displayStats.totalTwoYearSavings)}
              </div>
            </div>
            <div className="rd-stat-card">
              <div className="rd-stat-label">3-Year Savings</div>
              <div className="rd-stat-value" style={{ color: WS_STATUS.success.ink, ...PROJECTED }}>
                {formatCurrency(displayStats.totalThreeYearSavings)}
              </div>
            </div>
            {/* License renegotiation card — only shown when applicable */}
            {(() => {
              const source = filteredRecords;
              const renegoApps = source.filter(r => r.license_renegotiation);
              const totalLicenseSavings = renegoApps.reduce((s, r) => s + parseNum(r.license_savings), 0);
              return renegoApps.length > 0 ? (
                <div className="rd-stat-card" style={{ borderLeftColor: WS_STATUS.warning.ink }}>
                  <div className="rd-stat-label">License Renegotiation Savings</div>
                  <div className="rd-stat-value" style={{ color: WS_STATUS.warning.ink, ...PROJECTED }}>
                    {formatCurrency(totalLicenseSavings)}
                  </div>
                  <div style={{ ...wsType('meta'), color: WS_COLOR.textMuted, marginTop: 1 }}>
                    {renegoApps.length} app{renegoApps.length > 1 ? 's' : ''}
                  </div>
                </div>
              ) : null;
            })()}
          </div>
        )}

        {/* ── Filters ─────────────────────────────────────────────────────── */}
        {allRecords.length > 0 && (
          <div className="rd-filters-bar" style={{ marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <GroupMultiSelect
              testId="group-picker"
              label="Integration Group:"
              placeholder={ALL_GROUPS_LABEL}
              groups={groupOptions}
              value={filterGroupIds}
              onChange={setFilterGroupIds}
            />
            {/* Disposition multi-select */}
            <MultiSelectChips
              label="Disposition"
              options={dispositionOptions}
              selected={filterDispositions}
              onChange={setFilterDispositions}
              renderOption={v => (
                <span style={dispositionChipStyle(v === 'Pending' ? null : v)}>{v}</span>
              )}
              placeholder="All Dispositions"
            />

            {/* Application filter — chip-style, matches Disposition */}
            {(() => {
              const appOptions = Array.from(
                new Map(
                  allRecords
                    .filter(r => r.application_id)
                    .map(r => [r.application_id, r.application_name || r.application_id])
                ).entries()
              ).sort((a, b) => String(a[1]).localeCompare(String(b[1])));
              const ids = appOptions.map(([id]) => id);
              const labelById = new Map(appOptions);
              return (
                <MultiSelectChips
                  label="Application"
                  options={ids}
                  selected={filterApplicationIds}
                  onChange={setFilterApplicationIds}
                  getLabel={(id) => labelById.get(id) || id}
                  placeholder="All Applications"
                />
              );
            })()}

            {/* TCO Data filter — chip-style */}
            <MultiSelectChips
              label="TCO Data"
              options={['With TCO', 'Without TCO']}
              selected={filterTCOData}
              onChange={setFilterTCOData}
              placeholder="All Apps"
            />

            {/* TCO Status filter — chip-style */}
            <MultiSelectChips
              label="TCO Status"
              options={['Defined', 'Bundled', 'Free', 'Not Defined']}
              selected={filterTCOStatuses}
              onChange={setFilterTCOStatuses}
              placeholder="All Statuses"
            />

            {/* Clear filters */}
            {hasActiveFilters && (
              <button
                className="rd-btn rd-btn-ghost"
                onClick={() => {
                  setFilterApplicationIds([]);
                  setFilterGroupIds([]);
                  setFilterTCOData([]);
                  setFilterTCOStatuses([]);
                  setFilterDispositions([]);
                }}
              >
                Clear Filters
              </button>
            )}

            {hasActiveFilters && (
              <span style={{ ...wsType('label'), fontWeight: 400, color: WS_COLOR.textMuted, marginLeft: 'auto' }}>
                Showing {filteredRecords.length} of {allRecords.length} records
              </span>
            )}
          </div>
        )}
      </div>{/* end fixed-header-section */}

      {/* ── Scrollable data section ───────────────────────────────────────── */}
      <div className="scrollable-data-section" style={{ overflowX: 'auto' }}>

        {allRecords.length === 0 && !loading && (
          <div className="md-empty-state">
            <div className="md-empty-icon"><Icon icon={icons.Coins} size={24} /></div>
            <h3>No Benefit Realization Data Available</h3>
            <p>Benefit realization records will appear here once disposition cost analysis data has been created.</p>
            <button
              className="rd-btn rd-btn-primary"
              onClick={initializeFromDispositionAnalysis}
            >
              Initialize from Disposition Analysis
            </button>
          </div>
        )}

        {allRecords.length > 0 && (
          <div style={{ overflow: 'auto', flex: '1 1 auto', minHeight: 0 /* fills what the fixed header leaves (measured by PpScreen); no typed offset */, ...WS_CARD, paddingBottom: 14 }}>
            {filteredRecords.length > 0 ? (
              <table className="rd-table sticky-head-table" style={{ minWidth: '1800px', overflow: 'visible' /* the source's overflow:hidden made the table the sticky container — the anchored columns scrolled away */ }}>
                <thead>
                  <tr>
                    {/* Sticky cols */}
                    <th style={{ position: 'sticky', left: 0, width: '200px', maxWidth: '200px', boxSizing: 'border-box', backgroundClip: 'padding-box', backgroundColor: WS_COLOR.surfaceSubtle, borderRight: `2px solid ${WS_COLOR.border}`, minWidth: '200px', textAlign: 'left', zIndex: 5 }}>
                      Application
                    </th>
                    <th style={{ position: 'sticky', left: '200px', width: '140px', maxWidth: '140px', boxSizing: 'border-box', backgroundClip: 'padding-box', backgroundColor: WS_COLOR.surfaceSubtle, borderRight: `2px solid ${WS_COLOR.border}`, minWidth: '140px', textAlign: 'center', zIndex: 4 }}>
                      Disposition
                    </th>
                    {/* Data cols */}
                    <th style={{ textAlign: 'right', minWidth: '170px' }}>Total Annual Cost</th>
                    <th style={{ textAlign: 'right', minWidth: '170px' }}>Disposition Cost</th>
                    <th
                      style={{ textAlign: 'right', minWidth: '170px', position: 'relative', cursor: 'help' }}
                      onMouseEnter={() => {
                        if (tooltipTimer) clearTimeout(tooltipTimer);
                        const t = setTimeout(() => setShowTooltip(true), 1500);
                        setTooltipTimer(t);
                      }}
                      onMouseLeave={() => {
                        if (tooltipTimer) clearTimeout(tooltipTimer);
                        setShowTooltip(false);
                        setTooltipTimer(null);
                      }}
                    >
                      Future Run Rate Costs (Annual)
                    </th>
                    <th
                      style={{ textAlign: 'right', minWidth: '170px', position: 'relative', cursor: 'help' }}
                      onMouseEnter={() => {
                        if (plannedProjectTooltipTimer) clearTimeout(plannedProjectTooltipTimer);
                        const t = setTimeout(() => setShowPlannedProjectTooltip(true), 1500);
                        setPlannedProjectTooltipTimer(t);
                      }}
                      onMouseLeave={() => {
                        if (plannedProjectTooltipTimer) clearTimeout(plannedProjectTooltipTimer);
                        setShowPlannedProjectTooltip(false);
                        setPlannedProjectTooltipTimer(null);
                      }}
                    >
                      Planned Project Costs (One-Time)
                    </th>
                    {/* DW: numeric columns right-aligned (were centred) — DESIGN.md tabular figures. */}
                    <th style={{ textAlign: 'right', minWidth: '150px' }}>Net Savings</th>
                    <th style={{ textAlign: 'right', minWidth: '110px' }}>Net Savings %</th>
                    <th style={{ textAlign: 'right', minWidth: '150px' }}>Lease Buyback</th>
                    <th style={{ textAlign: 'right', minWidth: '180px', ...wsTone('warning') }}>Renegotiated License Cost</th>
                    <th style={{ textAlign: 'right', minWidth: '140px', ...wsTone('warning') }}>License Savings</th>
                    <th style={{ textAlign: 'right', minWidth: '150px' }}>Adjusted Savings</th>
                    <th style={{ textAlign: 'right', minWidth: '110px' }}>Adjusted %</th>
                    <th style={{ textAlign: 'right', minWidth: '110px' }}>Break Even</th>
                    <th style={{ textAlign: 'right', minWidth: '150px' }}>1-Year Savings</th>
                    <th style={{ textAlign: 'right', minWidth: '120px' }}>ROI % (1 yr)</th>
                    <th style={{ textAlign: 'right', minWidth: '150px' }}>2-Year Savings</th>
                    <th style={{ textAlign: 'right', minWidth: '120px' }}>ROI % (2 yr)</th>
                    <th style={{ textAlign: 'right', minWidth: '150px' }}>3-Year Savings</th>
                    <th style={{ textAlign: 'right', minWidth: '120px' }}>ROI % (3 yr)</th>
                    <th style={{ textAlign: 'center', minWidth: '70px' }}>Recalc</th>
                  </tr>
                </thead>

                <tbody>
                  {groupedRecords.map((record, idx) => {
                    const finalDisp = dispositionMap.get(record.application_id);
                    const firstOfGroup = idx === 0 || groupedRecords[idx - 1]!.grouping_id !== record.grouping_id;
                    return (
                      <React.Fragment key={`${record.grouping_id}|${record.application_id}`}>
                      {firstOfGroup && groupBusinessCaseRow(record.grouping_id, record.group_name)}
                      <tr data-testid="br-app-row">
                        {/* Application (sticky) */}
                        <td style={{
                          position: 'sticky', left: 0, width: '200px', maxWidth: '200px', boxSizing: 'border-box', backgroundClip: 'padding-box', padding: 6, backgroundColor: WS_COLOR.surface,
                          borderRight: `2px solid ${WS_COLOR.border}`, minWidth: '200px', zIndex: 5,
                        }}>
                          <div style={{ ...wsType('mono'), fontWeight: 600, color: WS_COLOR.text, paddingLeft: 12 }}>{record.application_id}</div>
                          {record.application_name && (
                            <div style={{ ...wsType('meta'), color: WS_COLOR.textMuted }}>{record.application_name}</div>
                          )}
                          {record.tco_status && record.tco_status !== 'Defined' && (
                            <span style={{
                              ...WS_CHIP,
                              ...wsTone(
                                record.tco_status === 'Bundled' ? 'info' :
                                record.tco_status === 'Free'    ? 'success' : 'error'
                              ),
                            }}>
                              {record.tco_status === 'Not Defined' ? 'TCO Not Defined' : record.tco_status}
                            </span>
                          )}
                          {/* Owner ruling 2026-11-04: no disposition / "Pending" pill under the app —
                              the Disposition column carries it. */}
                        </td>

                        {/* Disposition badge (sticky) */}
                        <td style={{
                          position: 'sticky', left: '200px', width: '140px', maxWidth: '140px', boxSizing: 'border-box', backgroundClip: 'padding-box', padding: 6, backgroundColor: WS_COLOR.surface,
                          borderRight: `2px solid ${WS_COLOR.border}`, textAlign: 'center', zIndex: 4, minWidth: '140px',
                        }}>
                          <span style={{
                            ...WS_CHIP,
                            backgroundColor: dispositionColor(record.disposition),
                            // M3 filled chip (§1.9.3 rule 2): text readable on the fill, 8px corner, no shadow.
                            color: matrixColor(record.disposition).onBase,
                          }}>
                            {record.disposition}
                            {record.disposition === 'RE-PLATFORM' && record.consolidate && ' (Consolidate)'}
                            {record.disposition === 'RE-PLATFORM' && record.replace_app && ' (Replace)'}
                          </span>
                        </td>

                        {/* Total Annual Cost */}
                        <td style={{
                          ...TABULAR, padding: 6,
                          ...(record.tco_missing ? { ...wsTone('error'), fontWeight: 600 } : {}),
                        }}>
                          {record.tco_missing
                            ? <span title="TCO is missing — no current run rate data defined">$0 (Missing)</span>
                            : formatCurrency(record.total_annual_cost_current)
                          }
                        </td>

                        {/* Editable fields */}
                        <td style={{ ...TABULAR, padding: 6 }}>
                          {renderEditableCell(record, 'total_disposition_cost', record.total_disposition_cost)}
                        </td>
                        <td style={{ ...TABULAR, padding: 6 }}>
                          {renderEditableCell(record, 'future_run_rate_annual', record.future_run_rate_annual)}
                        </td>
                        <td style={{ ...TABULAR, padding: 6 }}>
                          {renderEditableCell(record, 'planned_project_cost', record.planned_project_cost)}
                        </td>

                        {/* Net Savings */}
                        <td style={{ ...TABULAR, padding: 6, ...signedCell(annualSavingsOf(record)) }}>
                          <span style={PROJECTED}>{formatCurrency(annualSavingsOf(record))}</span>
                        </td>

                        {/* Net Savings % */}
                        <td style={{ ...TABULAR, padding: 6, ...signedCell(annualSavingsPct(record)) }}>
                          {formatPercentage(annualSavingsPct(record))}
                        </td>

                        {/* Lease Buyback */}
                        <td style={{ ...TABULAR, padding: 6 }}>
                          {renderEditableCell(record, 'lease_buyback_benefit', record.lease_buyback_benefit)}
                        </td>

                        {/* Renegotiated License Cost */}
                        <td style={{
                          ...TABULAR, padding: 6,
                          ...(record.license_renegotiation ? wsTone('warning') : { backgroundColor: WS_COLOR.surfaceSubtle }),
                        }}>
                          {record.license_renegotiation && record.disposition === 'RETAIN'
                            ? renderEditableCell(record, 'renegotiated_license_cost' as keyof BenefitRealizationRecord, record.renegotiated_license_cost || 0)
                            : <span style={{ ...wsType('meta'), color: WS_COLOR.textMuted }}>N/A</span>
                          }
                        </td>

                        {/* License Savings */}
                        <td style={{
                          ...TABULAR, padding: 6,
                          ...(record.license_renegotiation ? wsTone('warning') : { backgroundColor: WS_COLOR.surfaceSubtle }),
                          fontWeight: record.license_renegotiation ? 600 : 400,
                        }}>
                          {record.license_renegotiation
                            ? <span style={PROJECTED}>{formatCurrency(record.license_savings || 0)}</span>
                            : <span style={{ ...wsType('meta'), color: WS_COLOR.textMuted }}>N/A</span>
                          }
                        </td>

                        {/* Adjusted Savings */}
                        <td style={{ ...TABULAR, padding: 6, ...signedCell(calculateAdjustedSavingsValue(record)) }}>
                          <span style={PROJECTED}>{formatCurrency(calculateAdjustedSavingsValue(record))}</span>
                        </td>

                        {/* Adjusted % */}
                        <td style={{ ...TABULAR, padding: 6, ...signedCell(adjustedSavingsPct(record)) }}>
                          {formatPercentage(adjustedSavingsPct(record))}
                        </td>

                        {/* Break Even */}
                        <td style={{ ...TABULAR, padding: 6 }}>
                          {formatMonths(record.break_even_months_exact)}
                        </td>

                        {/* 1-Year Savings */}
                        <td style={{ ...TABULAR, padding: 6 }}>
                          <span style={PROJECTED}>{formatCurrency(calculateOneYearSavings(record))}</span>
                        </td>

                        {/* ROI % 1yr */}
                        <td style={{ ...TABULAR, padding: 6, fontWeight: 600, color: signedInk(calculateNYearROI(record, 1)) }}>
                          {formatROIPercentage(calculateNYearROI(record, 1))}
                        </td>

                        {/* 2-Year Savings */}
                        <td style={{ ...TABULAR, padding: 6 }}>
                          <span style={PROJECTED}>{formatCurrency(calculateTwoYearSavings(record))}</span>
                        </td>

                        {/* ROI % 2yr */}
                        <td style={{ ...TABULAR, padding: 6, fontWeight: 600, color: signedInk(calculateNYearROI(record, 2)) }}>
                          {formatROIPercentage(calculateNYearROI(record, 2))}
                        </td>

                        {/* 3-Year Savings */}
                        <td style={{ ...TABULAR, padding: 6 }}>
                          <span style={PROJECTED}>{formatCurrency(calculateThreeYearSavings(record))}</span>
                        </td>

                        {/* ROI % 3yr */}
                        <td style={{ ...TABULAR, padding: 6, fontWeight: 600, color: signedInk(calculateNYearROI(record, 3)) }}>
                          {formatROIPercentage(calculateNYearROI(record, 3))}
                        </td>

                        {/* Recalc button */}
                        <td style={{ textAlign: 'center', padding: 6 }}>
                          {!approvedApplications.has(record.application_id) && (
                            <IconButton
                              icon={icons.RefreshCw}
                              label="Recalculate all values for this application"
                              title="Recalculate all values for this application"
                              onClick={() => recalculateRecord(record)}
                            />
                          )}
                        </td>
                      </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>

                {/* Totals footer */}
                {filteredRecords.length > 0 && (
                  <tfoot>
                    <tr style={{ backgroundColor: WS_COLOR.surfaceSubtle, fontWeight: 600 }}>
                      <td style={{
                        position: 'sticky', left: 0, width: '200px', maxWidth: '200px', boxSizing: 'border-box', backgroundClip: 'padding-box', backgroundColor: WS_COLOR.surfaceSubtle,
                        borderRight: `2px solid ${WS_COLOR.border}`, zIndex: 5, minWidth: '200px',
                      }}>
                        <strong>TOTALS ({filteredRecords.length} apps)</strong>
                      </td>
                      <td style={{
                        position: 'sticky', left: '200px', width: '140px', maxWidth: '140px', boxSizing: 'border-box', backgroundClip: 'padding-box', backgroundColor: WS_COLOR.surfaceSubtle,
                        borderRight: `2px solid ${WS_COLOR.border}`, zIndex: 4, textAlign: 'center',
                      }}>
                        <strong>—</strong>
                      </td>
                      <td style={TABULAR}>
                        {formatCurrency(filteredRecords.reduce((s, r) => s + parseNum(r.total_annual_cost_current), 0))}
                      </td>
                      <td style={TABULAR}>
                        {formatCurrency(filteredRecords.reduce((s, r) => s + parseNum(r.total_disposition_cost), 0))}
                      </td>
                      <td style={TABULAR}>
                        <span style={PROJECTED}>{formatCurrency(filteredRecords.reduce((s, r) => s + parseNum(r.future_run_rate_annual), 0))}</span>
                      </td>
                      <td style={TABULAR}>
                        {formatCurrency(filteredRecords.reduce((s, r) => s + parseNum(r.planned_project_cost), 0))}
                      </td>
                      <td style={{ ...TABULAR, ...wsTone('success') }}>
                        <span style={PROJECTED}>{formatCurrency(filteredRecords.reduce((s, r) => s + annualSavingsOf(r), 0))}</span>
                      </td>
                      <td style={{ ...TABULAR, ...wsTone('success') }}>
                        {(() => {
                          const totalTCO = filteredRecords.reduce((s, r) => s + parseNum(r.total_annual_cost_current), 0);
                          const totalNet = filteredRecords.reduce((s, r) => s + annualSavingsOf(r), 0);
                          return `Portfolio: ${formatPercentage(totalTCO > 0 ? totalNet / totalTCO : 0)}`;
                        })()}
                      </td>
                      <td style={TABULAR}>
                        {formatCurrency(filteredRecords.reduce((s, r) => s + parseNum(r.lease_buyback_benefit), 0))}
                      </td>
                      <td style={{ ...TABULAR, ...wsTone('warning') }}>
                        {formatCurrency(filteredRecords.filter(r => r.license_renegotiation).reduce((s, r) => s + parseNum(r.renegotiated_license_cost), 0))}
                      </td>
                      <td style={{ ...TABULAR, ...wsTone('warning'), fontWeight: 600 }}>
                        <span style={PROJECTED}>{formatCurrency(filteredRecords.filter(r => r.license_renegotiation).reduce((s, r) => s + parseNum(r.license_savings), 0))}</span>
                      </td>
                      <td style={{ ...TABULAR, ...wsTone('success') }}>
                        <span style={PROJECTED}>{formatCurrency(filteredRecords.reduce((s, r) => s + calculateAdjustedSavingsValue(r), 0))}</span>
                      </td>
                      <td style={{ ...TABULAR, ...wsTone('success') }}>
                        Avg: {formatPercentage(filteredRecords.reduce((s, r) => s + adjustedSavingsPct(r), 0) / filteredRecords.length)}
                      </td>
                      <td style={TABULAR}>
                        {displayStats.avgBreakEvenMonths > 0
                          ? `${displayStats.avgBreakEvenMonths.toFixed(1)} mo`
                          : 'N/A'}
                      </td>
                      <td style={TABULAR}>
                        <span style={PROJECTED}>{formatCurrency(filteredRecords.reduce((s, r) => s + calculateOneYearSavings(r), 0))}</span>
                      </td>
                      <td style={TABULAR}>
                        {(() => {
                          const totalInv = filteredRecords.reduce((s, r) => s + parseNum(r.total_disposition_cost) + parseNum(r.planned_project_cost), 0);
                          const totalTCO = filteredRecords.reduce((s, r) => s + parseNum(r.total_annual_cost_current), 0);
                          const total1y  = filteredRecords.reduce((s, r) => s + calculateOneYearSavings(r), 0);
                          const denom = totalInv > 0 ? totalInv : totalTCO;
                          return `Portfolio: ${formatPortfolioROI(denom > 0 ? total1y / denom : 0)}`;
                        })()}
                      </td>
                      <td style={TABULAR}>
                        <span style={PROJECTED}>{formatCurrency(filteredRecords.reduce((s, r) => s + calculateTwoYearSavings(r), 0))}</span>
                      </td>
                      <td style={TABULAR}>
                        {(() => {
                          const totalInv = filteredRecords.reduce((s, r) => s + parseNum(r.total_disposition_cost) + parseNum(r.planned_project_cost), 0);
                          const totalTCO = filteredRecords.reduce((s, r) => s + parseNum(r.total_annual_cost_current), 0);
                          const total2y  = filteredRecords.reduce((s, r) => s + calculateTwoYearSavings(r), 0);
                          const denom = totalInv > 0 ? totalInv : totalTCO;
                          return `Portfolio: ${formatPortfolioROI(denom > 0 ? total2y / denom : 0)}`;
                        })()}
                      </td>
                      <td style={TABULAR}>
                        <span style={PROJECTED}>{formatCurrency(filteredRecords.reduce((s, r) => s + calculateThreeYearSavings(r), 0))}</span>
                      </td>
                      <td style={TABULAR}>
                        {(() => {
                          const totalInv = filteredRecords.reduce((s, r) => s + parseNum(r.total_disposition_cost) + parseNum(r.planned_project_cost), 0);
                          const totalTCO = filteredRecords.reduce((s, r) => s + parseNum(r.total_annual_cost_current), 0);
                          const total3y  = filteredRecords.reduce((s, r) => s + calculateThreeYearSavings(r), 0);
                          const denom = totalInv > 0 ? totalInv : totalTCO;
                          return `Portfolio: ${formatPortfolioROI(denom > 0 ? total3y / denom : 0)}`;
                        })()}
                      </td>
                      <td style={{ textAlign: 'center' }}>—</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            ) : (
              <div style={{ padding: '40px', textAlign: 'center', color: WS_COLOR.textMuted }}>
                <p style={wsType('body')}>No records match the selected filters.</p>
              </div>
            )}
          </div>
        )}

        {/* Cost Definitions note */}
        {allRecords.length > 0 && (
          <div style={{
            ...WS_CARD, marginTop: 12, padding: '10px 14px', backgroundColor: WS_COLOR.surfaceSubtle,
            ...wsType('meta'), color: WS_COLOR.textMuted,
            // On a short window the definitions give way first (and scroll), not the data.
            flexShrink: 1, minHeight: 0, overflow: 'auto',
          }}>
            <div style={{ ...wsType('meta'), fontWeight: 600, marginBottom: 4, color: WS_COLOR.text }}>Cost Definitions</div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {[
                  ['TCO (Total Annual Cost)', 'Total Annual Cost — what you pay today to run/host the application. Sourced from Costing Analysis / Current Run Rate data (read-only).', 'Recurring (Annual)'],
                  ['DC (Disposition Cost)', 'The migration/disposition execution itself — data migration, code refactoring, testing, cutover downtime costs. Sourced from Disposition Cost Analysis (read-only).', 'One-Time'],
                  ['FRC (Future Run Rate)', 'What you will pay every year going forward in the new environment — cloud instances, managed services, cloud licenses, support contracts.', 'Recurring (Annual)'],
                  ['PPC (Planned Project Cost)', 'One-time investments around the migration that are not the migration itself — new monitoring tools, staff retraining, security audit/certification, etc.', 'One-Time'],
                ].map(([label, desc, type]) => (
                  <tr key={label}>
                    {/* DW: the module's table rule sets 14px / 40px cells; this note stays at meta size. */}
                    <td style={{ ...wsType('meta'), height: 'auto', color: WS_COLOR.textMuted, padding: '3px 8px 3px 0', fontWeight: 600, whiteSpace: 'nowrap', verticalAlign: 'top', width: 160 }}>{label}</td>
                    <td style={{ ...wsType('meta'), height: 'auto', color: WS_COLOR.textMuted, padding: '3px 8px', verticalAlign: 'top' }}>{desc}</td>
                    <td style={{ ...wsType('meta'), height: 'auto', color: WS_COLOR.textMuted, padding: '3px 0 3px 8px', whiteSpace: 'nowrap', verticalAlign: 'top', fontStyle: 'italic', textAlign: 'right' }}>{type}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>{/* end scrollable-data-section */}

      {/* ── Tooltips (rendered outside table to avoid overflow clip) ─────── */}
      {showTooltip && (
        <div style={{
          position: 'fixed', top: 200, left: '50%', transform: 'translateX(-50%)',
          backgroundColor: WS_COLOR.surface, color: WS_COLOR.text, padding: 12, borderRadius: WS_RADIUS.field,
          ...wsType('meta'), maxWidth: 450, zIndex: 10000, boxShadow: WS_COLOR.menuShadow,
          border: `1px solid ${WS_COLOR.border}`, whiteSpace: 'normal',
        }}>
          <div style={{ marginBottom: 8, fontWeight: 600 }}>Future Run-Rate:</div>
          <div style={{ lineHeight: 1.4 }}>
            Estimated annual cost to run/support rationalized functionality after initiative is complete
            (e.g. Retire = $0, Re-Platform = additional licenses, support, etc. for rationalizing app).
          </div>
        </div>
      )}

      {showPlannedProjectTooltip && (
        <div style={{
          position: 'fixed', top: 200, left: '50%', transform: 'translateX(-50%)',
          backgroundColor: WS_COLOR.surface, color: WS_COLOR.text, padding: 12, borderRadius: WS_RADIUS.field,
          ...wsType('meta'), maxWidth: 450, zIndex: 10000, boxShadow: WS_COLOR.menuShadow,
          border: `1px solid ${WS_COLOR.border}`, whiteSpace: 'normal',
        }}>
          <div style={{ marginBottom: 8, fontWeight: 600 }}>Planned Project Cost (One-Time):</div>
          <div style={{ lineHeight: 1.6 }}>
            One-time costs incurred to keep application viable if it were to be Retained, Re-Platformed, or Re-Designed.
            <br /><br />
            For applications in <strong>Retire</strong> disposition: this cost may represent the estimated investment
            required for a successor application that will take over the function or data from the retiring application.
          </div>
        </div>
      )}
      {dialogs}
    </div>
  );
};

export default BenefitRealization;
